import { RADIUS, innerRadius, TEXT_MUTED } from '../../lib/medievalTheme';
import { getClass } from '../../utils/arena/classes';
import { t } from '../../localization/t';

const ACCENT = getClass('oracle').accent;

// A vial of sight: an iron housing with a gold rim, a fill breathing behind
// the glass, a notch every `notch` units and a bright meniscus at the
// fill's edge. The Oracle's Foresight and the omen's progress both pour
// into it. Sizes in cqw; `color` is the fill's hue.
export function VialGauge({ value = 0, max = 10, color = ACCENT, height = 0.875, notch = 5, className = '', style }) {
  const pct = max > 0 ? Math.min(1, value / max) * 100 : 0;
  const notches = notch > 0 && max / notch <= 12 ? Math.floor(max / notch) - (max % notch === 0 ? 1 : 0) : 0;
  return (
    <div
      className={`relative ${className}`}
      style={{
        height: `${height}cqw`,
        padding: '0.1875cqw',
        borderRadius: RADIUS.full,
        background: 'linear-gradient(180deg, #0c0a08 0%, #1c1710 100%)',
        boxShadow: 'inset 0 1px 0 rgba(0,0,0,0.8), inset 0 -1px 0 rgba(255,235,180,0.08), 0 0 0 1px rgba(212,168,67,0.55), 0 0 0 2px rgba(0,0,0,0.6), 0 0.25cqw 0.75cqw rgba(0,0,0,0.6)',
        ...style,
      }}
    >
      <div className="relative w-full h-full overflow-hidden" style={{ borderRadius: innerRadius(RADIUS.full, '0.1875cqw'), background: '#07090d', boxShadow: 'inset 0 0.0625cqw 0.25cqw rgba(0,0,0,0.9)' }}>
        <div
          className="vh-foresight-fill absolute inset-y-0 left-0"
          style={{
            width: `${pct}%`,
            borderRadius: 'inherit',
            background: `linear-gradient(180deg, #ffffff 0%, ${color} 35%, ${color} 100%)`,
            backgroundBlendMode: 'normal',
            boxShadow: `0 0 0.75cqw ${color}aa, inset 0 -1px 0 rgba(0,0,0,0.35)`,
            transitionProperty: 'width',
            transitionDuration: '600ms',
            transitionTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)',
          }}
        />
        {pct > 0 && pct < 100 ? (
          <div className="absolute inset-y-0" style={{ left: `calc(${pct}% - 1px)`, width: '2px', background: '#eef6ff', boxShadow: `0 0 0.5cqw #ffffff, 0 0 1cqw ${color}` }} />
        ) : null}
        {Array.from({ length: notches }, (_, i) => (
          <div key={i} className="absolute inset-y-0" style={{ left: `${((i + 1) * notch / max) * 100}%`, width: '1px', background: 'rgba(0,0,0,0.55)' }} />
        ))}
      </div>
    </div>
  );
}

// The Oracle's banked Foresight: label, vial, count.
// `size` scales the type and the vial together (1 = the reading's gauge).
export default function ForesightGauge({ value = 0, max = 10, width = 22, size = 1 }) {
  return (
    <div className="flex items-center" style={{ gap: `${0.875 * size}cqw`, width: `${width}cqw` }} data-testid="foresight-gauge">
      <span className="arena-heading" style={{ color: ACCENT, fontSize: `${0.9375 * size}cqw`, letterSpacing: '0.04em', textShadow: `0 0 0.75cqw ${ACCENT}66` }}>
        {t('Foresight')}
      </span>
      <VialGauge className="flex-1" value={value} max={max} height={0.875 * size} />
      <span className="arena-heading tabular-nums" style={{ color: value > 0 ? '#dbeeff' : TEXT_MUTED, fontSize: `${0.9375 * size}cqw`, minWidth: `${3.25 * size}cqw`, textAlign: 'right' }}>
        {value} / {max}
      </span>
    </div>
  );
}
